/**
 * Client-side flowfield NN inference (FXC-9009) — a faithful port of the coordinate-MLP so the
 * workbench can predict + render the flowfield in-browser (no server round-trip), letting it react
 * to geometry changes live. Same model as nn_flow_server.py:
 *
 *   raw9 = [alpha, mach, re/1e6, blow, d_main, d_vane, d_flap, x, z]
 *   xb   = (raw9 - xmu) / xsd
 *   feat = [xb, sin(xb[4:9]·B), cos(xb[4:9]·B)]            (105-dim Fourier features)
 *   h    = SiLU(Wn·h + bn) for each layer (no SiLU on the last)  ->  [Cp, u, v, Mach]·ysd + ymu
 *
 * Produces the SAME FlowMesh {points,tris,vel,mach,machRange} the LIC renderer + the RANS path use.
 */
import type { FlowMesh } from '../foil/FlowVisLayer';

function b64f32(s: string): Float32Array {
  const bin = atob(s);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Float32Array(buf.buffer);
}

interface Layer { in: number; out: number; W: Float32Array; b: Float32Array; }

/** Checkpoint feature-schema stamp (FXC-9009): which raw-input contract this checkpoint was
 *  trained against. "raw9" = the legacy fixed [alpha, mach, re/1e6, blow, d_main, d_vane,
 *  d_flap, x, z] layout; "v2" = the schema-driven `raw_features` token list. */
export type NNSchemaVersion = 'raw9' | 'v2';

export interface NN {
  layers: Layer[]; B: Float32Array; Brows: number; Bcols: number;
  xmu: Float32Array; xsd: Float32Array; ymu: Float32Array; ysd: Float32Array;
  inDim: number; channels: string[];
  /** v2 feature contract (FXC-9009). `rawFeatures` names every raw column (see nn_features.py
   *  raw_features()); absent ⇒ the legacy raw9 layout. `rawDim` is the raw-vector width; `fourierStart`
   *  is the first raw col fed to the Fourier lift (legacy 4 = the d_* + x,z block; v2 = fourier_cols[0]). */
  rawFeatures?: string[]; rawDim: number; fourierStart: number;
  /** Which feature schema `loadNN` detected — surfaced on the NN badge so a mismatched
   *  checkpoint is visible, not a silent wrong-features inference (FXC-9009). */
  schemaVersion: NNSchemaVersion;
  /** Raw-input width (== rawDim; named per the badge contract: "N raw features"). */
  rawFeatureCount: number;
}

export function loadNN(json: any): NN {
  // ── schema detection (FXC-9009) ─────────────────────────────────────────────
  // A checkpoint's raw-feature layout is a hard contract: feeding raw9-ordered inputs to a
  // v2-trained network (or vice versa) produces confidently-wrong fields with no error. So
  // detect the schema EXPLICITLY and refuse anything we don't recognise — a thrown error at
  // weight-load (which every caller already handles as "NN unavailable") is honest; a silent
  // raw9 fallback is not. `xmu` is the per-raw-column normalisation vector, so its width is
  // the checkpoint's true raw dimension — the cross-check both branches validate against.
  const xmu = b64f32(json.xmu);
  const rawFeaturesField = json.raw_features;
  let rawFeatures: string[] | undefined;
  let schemaVersion: NNSchemaVersion;
  if (rawFeaturesField != null) {
    // v2: raw_features must be a non-empty string list whose length matches the stats width.
    if (!Array.isArray(rawFeaturesField) || rawFeaturesField.length === 0
        || !rawFeaturesField.every((t: unknown) => typeof t === 'string')) {
      throw new Error('NN checkpoint schema unknown: raw_features present but malformed — refusing the silent raw9 fallback');
    }
    if (rawFeaturesField.length !== xmu.length) {
      throw new Error(`NN checkpoint schema mismatch: ${rawFeaturesField.length} raw_features vs ${xmu.length} normalisation stats`);
    }
    rawFeatures = rawFeaturesField as string[];
    schemaVersion = 'v2';
  } else {
    // legacy raw9: no schema shipped, so the ONLY layout we may assume is the fixed 9-wide
    // one — anything else is a newer export missing its raw_features, not a raw9 model.
    if (xmu.length !== 9) {
      throw new Error(`NN checkpoint schema unknown: no raw_features and ${xmu.length} raw columns (legacy raw9 needs exactly 9)`);
    }
    schemaVersion = 'raw9';
  }
  const rawDim = rawFeatures ? rawFeatures.length : 9;
  const fc: number[] = Array.isArray(json.fourier_cols) ? json.fourier_cols : [4, 9];
  return {
    layers: json.layers.map((l: any) => ({ in: l.in, out: l.out, W: b64f32(l.W), b: b64f32(l.b) })),
    B: b64f32(json.Bmat), Brows: json.Brows, Bcols: json.Bcols,
    xmu, xsd: b64f32(json.xsd), ymu: b64f32(json.ymu), ysd: b64f32(json.ysd),
    inDim: json.in_dim, channels: json.channels,
    rawFeatures, rawDim, fourierStart: fc[0] ?? 4,
    schemaVersion, rawFeatureCount: rawDim,
  };
}

const silu = (x: number) => x / (1 + Math.exp(-x));

/** Build the Fourier features from the raw vector (flat N×rawDim → flat N×inDim): the rawDim raw cols
 *  pass through, then sin/cos of the (Brows cols starting at fourierStart) projected through B. Drives
 *  its dims from the checkpoint so the legacy raw9 (rawDim 9, fourierStart 4) and the v2 layout both
 *  work unchanged. */
export function buildFeatures(nn: NN, raw: Float32Array, N: number): Float32Array {
  const { xmu, xsd, B, Brows, Bcols, inDim, rawDim, fourierStart } = nn;
  const feat = new Float32Array(N * inDim);
  const xb = new Float32Array(rawDim);
  for (let n = 0; n < N; n++) {
    const ro = n * rawDim, fo = n * inDim;
    for (let i = 0; i < rawDim; i++) { const v = (raw[ro + i]! - xmu[i]!) / xsd[i]!; xb[i] = v; feat[fo + i] = v; }
    for (let c = 0; c < Bcols; c++) {
      let p = 0;
      for (let r = 0; r < Brows; r++) p += xb[fourierStart + r]! * B[r * Bcols + c]!;
      feat[fo + rawDim + c] = Math.sin(p);
      feat[fo + rawDim + Bcols + c] = Math.cos(p);
    }
  }
  return feat;
}

/** JS MLP (the matmul stack) on features → RAW outputs (flat N×NC, NOT denormalized). This is the
 *  exact computation the WebGPU path must reproduce — the harness compares against it. */
export function mlpRaw(nn: NN, feat: Float32Array, N: number): Float32Array {
  let h = feat; let hw = nn.inDim;
  for (let li = 0; li < nn.layers.length; li++) {
    const L = nn.layers[li]!; const last = li === nn.layers.length - 1;
    const out = new Float32Array(N * L.out);
    for (let n = 0; n < N; n++) {
      const ho = n * hw, oo = n * L.out;
      for (let o = 0; o < L.out; o++) {
        let s = L.b[o]!; const wo = o * L.in;
        for (let i = 0; i < L.in; i++) s += L.W[wo + i]! * h[ho + i]!;
        out[oo + o] = last ? s : silu(s);
      }
    }
    h = out; hw = L.out;
  }
  return h;
}

/** Denormalize raw MLP outputs in place (flat N×NC → physical [Cp,u,v,Mach]). */
export function denorm(nn: NN, out: Float32Array, N: number): Float32Array {
  const { ymu, ysd } = nn; const NC = nn.layers[nn.layers.length - 1]!.out;
  for (let n = 0; n < N; n++) for (let k = 0; k < NC; k++) out[n * NC + k] = out[n * NC + k]! * ysd[k]! + ymu[k]!;
  return out;
}

/** Forward a batch of raw9 inputs (flat, N×9) → flat N×4 [Cp,u,v,Mach] (JS, verified). */
export function forward(nn: NN, raw: Float32Array, N: number): Float32Array {
  return denorm(nn, mlpRaw(nn, buildFeatures(nn, raw, N), N), N);
}

/** Signed distance from every (x,z) point to a closed contour (negative inside). */
export function signedDist(px: Float32Array, pz: Float32Array, contour: number[][]): Float32Array {
  const N = px.length; const out = new Float32Array(N);
  const C = contour.slice();
  if (C.length && (C[0]![0] !== C[C.length - 1]![0] || C[0]![1] !== C[C.length - 1]![1])) C.push(C[0]!);
  const M = C.length;
  for (let n = 0; n < N; n++) {
    const x = px[n]!, y = pz[n]!; let best = Infinity, inside = false; let j = M - 1;
    for (let i = 0; i < M; i++) {
      const Ci = C[i]!, Cj = C[j]!;
      const xi = Ci[0]!, yi = Ci[1]!, xj = Cj[0]!, yj = Cj[1]!;
      const ax = xj, ay = yj, bx = xi, by = yi;       // segment (C[j] -> C[i])
      const dx = bx - ax, dy = by - ay; const L2 = dx * dx + dy * dy || 1e-12;
      let t = ((x - ax) * dx + (y - ay) * dy) / L2; t = t < 0 ? 0 : t > 1 ? 1 : t;
      const qx = ax + t * dx - x, qy = ay + t * dy - y; const d2 = qx * qx + qy * qy;
      if (d2 < best) best = d2;
      // ray-cast inside test
      if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-30) + xi)) inside = !inside;
      j = i;
    }
    out[n] = (inside ? -1 : 1) * Math.sqrt(best);
  }
  return out;
}

/** Per-foil body-fitted coordinate of every (x,z) point vs ONE closed contour. The contour is
 *  assumed Selig-ordered (TE → upper → LE → lower → TE) so `s` reads as the conventional 0→1 surface
 *  station. The network's local frame per element is the (normal, tangential) pair:
 *    - dist : signed distance (− inside) — the normal offset                       (existing raw9 feature)
 *    - s    : Selig arc-length [0,1] of the NEAREST surface point — the tangential station
 *  `s` says which part of the surface is nearest (LE suction vs cove vs TE slot) AND which side the
 *  point is on (upper ⇒ s<0.5, lower ⇒ s>0.5), so distance + s is a complete body-fitted coordinate.
 *  Computed in one pass so it stays cheap at grid resolution. Mirror of build_foil_coords() in
 *  train_flowfield_mlp.py — the two MUST agree numerically (same contour ordering, same conventions). */
export interface FoilCoord { dist: Float32Array; s: Float32Array; }
export function foilCoords(px: Float32Array, pz: Float32Array, contour: number[][]): FoilCoord {
  const N = px.length;
  const dist = new Float32Array(N), s = new Float32Array(N);
  const C = contour.slice();
  if (C.length && (C[0]![0] !== C[C.length - 1]![0] || C[0]![1] !== C[C.length - 1]![1])) C.push(C[0]!);
  const M = C.length;
  // cumulative arc length per vertex (cum[0]=0 … cum[M-1]=perimeter) → the Selig station of any point.
  const cum = new Float64Array(M);
  for (let i = 1; i < M; i++) cum[i] = cum[i - 1]! + Math.hypot(C[i]![0]! - C[i - 1]![0]!, C[i]![1]! - C[i - 1]![1]!);
  const total = cum[M - 1]! || 1e-12;
  for (let n = 0; n < N; n++) {
    const x = px[n]!, y = pz[n]!; let best = Infinity, inside = false, bestArc = 0; let j = M - 1;
    for (let i = 0; i < M; i++) {
      const ax = C[j]![0]!, ay = C[j]![1]!, bx = C[i]![0]!, by = C[i]![1]!;   // segment C[j] -> C[i]
      const dx = bx - ax, dy = by - ay; const L2 = dx * dx + dy * dy || 1e-12;
      let t = ((x - ax) * dx + (y - ay) * dy) / L2; t = t < 0 ? 0 : t > 1 ? 1 : t;
      const qx = ax + t * dx - x, qy = ay + t * dy - y; const d2 = qx * qx + qy * qy;
      if (d2 < best) { best = d2; bestArc = cum[j]! + t * Math.sqrt(L2); }   // arc at the nearest point
      if (((ay > y) !== (by > y)) && (x < ((bx - ax) * (y - ay)) / (by - ay + 1e-30) + ax)) inside = !inside;
      j = i;
    }
    dist[n] = (inside ? -1 : 1) * Math.sqrt(best);
    s[n] = bestArc / total;
  }
  return { dist, s };
}

/** One design point's conditions for the NN. Legacy raw9 reads only alpha/mach/re/blow; the v2 schema
 *  additionally reads the operation knobs (deploy/flaperon) and the geometry params (`geom`, keyed by
 *  the raw_features token, e.g. {gap_flap, overlap_flap, le_radius_main, camber_flap}). */
export interface NNCond {
  alpha: number; mach: number; re: number; blow: number;
  deploy?: number; flaperon?: number; geom?: Record<string, number>;
}

export const FOIL_NAMES = ['main', 'vane', 'flap'] as const;

/** Assemble the raw input matrix (flat N×rawDim) for a batch of points at ONE design point. Honors the
 *  checkpoint's v2 `rawFeatures` schema (mirror of nn_features.py raw_features); falls back to the legacy
 *  raw9 layout — byte-identical to the old inline builders — when no schema is present, so legacy
 *  checkpoints are unaffected. Per-foil {d,s} come from foilCoords; a missing element defaults d=10
 *  (far), s=0. Used by predictFlowMesh (grid) and nnSurfaceForces (surface points). */
export function buildRaw(
  nn: NN, cond: NNCond, elements: { name: string; contour: number[][] }[],
  px: Float32Array, pz: Float32Array, N: number,
): Float32Array {
  const find = (nm: string) => elements.find((e) => e.name === nm)?.contour;
  if (!nn.rawFeatures) {
    // legacy raw9 = [alpha, mach, re/1e6, blow, d_main, d_vane, d_flap, x, z]
    const dist = FOIL_NAMES.map((nm) => { const c = find(nm); return c && c.length >= 3 ? signedDist(px, pz, c) : null; });
    const raw = new Float32Array(N * 9); const [d0, d1, d2] = dist;
    for (let k = 0; k < N; k++) {
      const o = k * 9;
      raw[o] = cond.alpha; raw[o + 1] = cond.mach; raw[o + 2] = cond.re / 1e6; raw[o + 3] = cond.blow;
      raw[o + 4] = d0 ? d0[k]! : 10; raw[o + 5] = d1 ? d1[k]! : 10; raw[o + 6] = d2 ? d2[k]! : 10;
      raw[o + 7] = px[k]!; raw[o + 8] = pz[k]!;
    }
    return raw;
  }
  // v2 schema-driven: roles come from the schema's "role:d"/"role:s" tokens (any role vocabulary), so a
  // config fills the roles it has and absent roles default to far. Per-role body-fitted coord once.
  const rf = nn.rawFeatures; const D = rf.length;
  const roles = new Set<string>();
  for (const t of rf) { const ci = t.indexOf(':'); if (ci > 0) roles.add(t.slice(0, ci)); }
  const coords: Record<string, FoilCoord | null> = {};
  for (const nm of roles) { const c = find(nm); coords[nm] = c && c.length >= 3 ? foilCoords(px, pz, c) : null; }
  const scalar = (tok: string): number => {
    switch (tok) {
      case 'alpha': return cond.alpha; case 'mach': return cond.mach; case 're_e6': return cond.re / 1e6;
      case 'blow': return cond.blow; case 'deploy': return cond.deploy ?? 0; case 'flaperon': return cond.flaperon ?? 0;
      default: return cond.geom?.[tok] ?? 0;   // geometry params (gap_flap, le_radius_main, …)
    }
  };
  const raw = new Float32Array(N * D);
  for (let j = 0; j < D; j++) {
    const tok = rf[j]!;
    if (tok === 'x') { for (let k = 0; k < N; k++) raw[k * D + j] = px[k]!; continue; }
    if (tok === 'z') { for (let k = 0; k < N; k++) raw[k * D + j] = pz[k]!; continue; }
    const ci = tok.indexOf(':');
    if (ci > 0) {                                   // per-foil "name:d" / "name:s"
      const co = coords[tok.slice(0, ci)]; const isS = tok.slice(ci + 1) === 's';
      for (let k = 0; k < N; k++) raw[k * D + j] = co ? (isS ? co.s[k]! : co.dist[k]!) : (isS ? 0 : 10);
      continue;
    }
    const v = scalar(tok);                          // conditions + geometry params (constant per point)
    for (let k = 0; k < N; k++) raw[k * D + j] = v;
  }
  return raw;
}

export interface NNFlowOpts { clip?: [number, number, number, number]; W?: number; H?: number; }

export type MlpFn = (nn: NN, feat: Float32Array, N: number) => Float32Array | Promise<Float32Array>;

/** Predict the flowfield for a condition + element contours → a FlowMesh (foil cells removed).
 *  `mlpFn` is the matmul stack (default = verified JS `mlpRaw`; pass the WebGPU one for speed). */
export async function predictFlowMesh(
  nn: NN, cond: NNCond,
  elements: { name: string; contour: number[][] }[], opts: NNFlowOpts = {},
  mlpFn: MlpFn = mlpRaw,
): Promise<FlowMesh> {
  const [X0, X1, Z0, Z1] = opts.clip ?? [-0.7, 1.7, -0.78, 0.62];
  const W = opts.W ?? 140, H = opts.H ?? 92;
  const px = new Float32Array(W * H), pz = new Float32Array(W * H);
  for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
    const k = j * W + i;
    px[k] = X0 + (X1 - X0) * i / (W - 1);
    pz[k] = Z0 + (Z1 - Z0) * j / (H - 1);
  }
  const ci = (n: string) => elements.find((e) => e.name === n)?.contour;
  const names = ['main', 'vane', 'flap'];
  const dist = names.map((n) => { const c = ci(n); return c && c.length >= 3 ? signedDist(px, pz, c) : null; });
  const ci2 = nn.channels;
  const raw = buildRaw(nn, cond, elements, px, pz, W * H);
  const pred = denorm(nn, await mlpFn(nn, buildFeatures(nn, raw, W * H), W * H), W * H);
  const iCp = ci2.indexOf('Cp'), iu = ci2.indexOf('u'), iv = ci2.indexOf('v'), iM = ci2.indexOf('Mach');
  const NC = ci2.length;
  // Inside-foil flag: damp the field there (the FoilGeometryCanvas overlay covers the foils).
  const inside = new Uint8Array(W * H);
  for (let k = 0; k < W * H; k++) {
    let dmin = Infinity; for (const d of dist) if (d) dmin = Math.min(dmin, d[k]!);
    inside[k] = dmin < 0 ? 1 : 0;
  }
  // FULL-grid triangulation — NO foil hole (the old hole produced a jagged staircase at grid res).
  // The geometry overlay hides the foils; inside-foil velocity is damped so the LIC stays calm there.
  const tris: number[] = [];
  for (let j = 0; j < H - 1; j++) for (let i = 0; i < W - 1; i++) {
    const tl = j * W + i, tr = tl + 1, bl = tl + W, br = bl + 1;
    tris.push(tl, bl, tr, tr, bl, br);
  }
  const np = W * H; const points = new Array(np * 2), vel = new Array(np * 2), mach = new Array(np);
  let mmin = Infinity, mmax = -Infinity;
  for (let k = 0; k < np; k++) {
    points[k * 2] = px[k]; points[k * 2 + 1] = pz[k];
    const d = inside[k] ? 0 : 1;
    vel[k * 2] = pred[k * NC + iu]! * d; vel[k * 2 + 1] = pred[k * NC + iv]! * d;
    const m = pred[k * NC + iM]!; mach[k] = m; if (m < mmin) mmin = m; if (m > mmax) mmax = m;
  }
  void iCp;
  return { points, tris, vel, mach, machRange: [mmin, mmax] };
}
